import React from 'react';
import type { MenuDayItemDto } from '@core/interfaces/api';
import { sortCategories } from '../../components/menu-item-utils';
import {
  type MenuTemplateProps,
  BREAKFAST_MAIN_CATS,
  BREAKFAST_BEVERAGE_CATS,
  LEFT_ALIGNED_MENU_DESCRIPTION_STYLE,
  FitToPage,
  cleanItems,
  extractAlwaysAvailableItems,
  formatDisplayDate,
  groupByCategory,
} from '../shared/menu-print-shared';

function BreakfastLeftColumn({ items }: { items: MenuDayItemDto[] }) {
  const grouped = groupByCategory(items);
  const sideCats = sortCategories(
    Object.keys(grouped).filter((c) => !BREAKFAST_MAIN_CATS.has(c) && !BREAKFAST_BEVERAGE_CATS.has(c)),
  );

  return (
    <div style={{
      flex: '1.3',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid #ccc',
      paddingRight: '16px',
    }}>
      {sideCats.map((cat) => (
        <div key={cat} style={{ marginBottom: '12px' }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '17px',
            textAlign: 'center',
            marginBottom: '4px',
          }}>
            {cat}
          </div>
          {grouped[cat].map((item, idx) => (
            <div key={idx} style={{
              fontStyle: 'italic',
              fontSize: '10px',
              color: '#222',
              textAlign: 'center',
              lineHeight: '1.3',
            }}>
              {item.RecipeName}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function BreakfastRightColumn({ items }: { items: MenuDayItemDto[] }) {
  const grouped = groupByCategory(items);
  const mainItems = sortCategories(Object.keys(grouped).filter((c) => BREAKFAST_MAIN_CATS.has(c)))
    .flatMap((c) => grouped[c]);
  const beverageItems = Object.keys(grouped)
    .filter((c) => BREAKFAST_BEVERAGE_CATS.has(c))
    .flatMap((c) => grouped[c]);

  return (
    <div style={{
      flex: '0.7',
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: '4px',
    }}>
      <div style={{
        fontWeight: 'bold',
        fontSize: '17px',
        textAlign: 'center',
        marginBottom: '10px',
      }}>
        Main Dishes
      </div>
      {mainItems.map((item, idx) => (
        <div key={idx} style={{ marginBottom: '10px' }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {item.RecipeName}
          </div>
          {item.RecipeDescription ? (
            <div style={{
              ...LEFT_ALIGNED_MENU_DESCRIPTION_STYLE,
              fontStyle: 'italic',
              fontSize: '10px',
              color: '#333',
              lineHeight: '1.4',
              marginTop: '1px',
            }}>
              {item.RecipeDescription}
            </div>
          ) : null}
        </div>
      ))}
      {beverageItems.length > 0 && (
        <div style={{
          marginTop: '14px',
          borderTop: '0.5px solid #ccc',
          paddingTop: '10px',
        }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '17px',
            textAlign: 'center',
            marginBottom: '6px',
          }}>
            Beverages
          </div>
          <div style={{
            fontStyle: 'italic',
            fontSize: '10px',
            color: '#333',
            textAlign: 'center',
            lineHeight: '1.3',
          }}>
            {beverageItems.map((i) => i.RecipeName).join(', ')}
          </div>
        </div>
      )}
    </div>
  );
}

export function BreakfastTemplate({
  communityName,
  date,
  showDate = true,
  sections,
  alwaysAvailable,
}: MenuTemplateProps) {
  const allItems = cleanItems(sections.flatMap((s) => s.items));
  const alwaysItems = cleanItems(extractAlwaysAvailableItems(alwaysAvailable));

  const titleBlock = (
    <div style={{ textAlign: 'center', marginBottom: '6px' }}>
      <div style={{
        fontSize: '26px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '2px',
      }}>
        Breakfast Menu
      </div>
      {/* Rule */}
      <div style={{ borderBottom: '1.5px solid #000', marginTop: '6px' }} />
    </div>
  );

  const footerBlock = showDate && date ? (
    <div style={{ textAlign: 'center', marginTop: '8px' }}>
      <div style={{
        fontSize: '10px',
        color: '#888',
      }}>
        {formatDisplayDate(date)}
      </div>
    </div>
  ) : null;

  return (
    <FitToPage fixedContent={titleBlock} fixedFooter={footerBlock}>
      {/* Two columns */}
      <div style={{ display: 'flex', gap: '20px', flex: 1, marginTop: '16px' }}>
        <BreakfastLeftColumn items={allItems} />
        <BreakfastRightColumn items={allItems} />
      </div>

      {/* Always available */}
      {alwaysItems.length > 0 && (
        <div style={{
          marginTop: '12px',
          borderTop: '0.5px solid #ccc',
          paddingTop: '8px',
          textAlign: 'center',
        }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '9px',
            color: '#555',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '3px',
          }}>
            Always Available
          </div>
          <div style={{
            fontStyle: 'italic',
            fontSize: '9px',
            color: '#555',
            lineHeight: '1.5',
          }}>
            {alwaysItems.map((i) => i.RecipeName).join('  \u2022  ')}
          </div>
        </div>
      )}
    </FitToPage>
  );
}
